/** Turns a history round into what the list draws. Reads the round and
    its bets, never `roundOutcome`, so any game can use it. */
import { formatAmount, formatMoney } from '../lib/currency';

/** The fields read off a bet. Every game's bet type has these. */
export interface HistoryBetLike {
  id: string;
  amount?: number;
  winAmount?: number;
  status?: string;
  selectionName?: string | null;
}

/** The fields read off a round. Some games type fewer than they get. */
export interface HistoryItemLike {
  id: string;
  roundId?: string;
  externalRoundId?: string;
  roundTime?: string;
  outcome?: string;
  bets?: HistoryBetLike[];
}

/** Rows per page. The table is drawn for eight. */
export const HISTORY_PAGE_SIZE = 8;

/** Judged on the money, not the backend's `outcome`, which says WIN for
    a round that broke even. */
export type HistoryResult = 'win' | 'loss' | 'tie' | 'void';

/** `19 Sep` and `5:37 PM`. */
export interface RoundTime {
  day: string;
  clock: string;
}

export interface HistoryRow {
  id: string;
  /** The id the player sees, e.g. `JM1000000231`. */
  roundId: string;
  time: RoundTime;
  stake: number;
  payout: number;
  net: number;
  /** Payout over stake. */
  multiplier: number;
  result: HistoryResult;
  /** What was picked. Only read when the round has one bet. */
  selection: string | null;
  betCount: number;
}

/** One bet inside a round, for the per-bet breakdown. */
export interface BetLine {
  id: string;
  stake: number;
  multiplier: number;
  payout: number;
  net: number;
  result: HistoryResult;
  selection: string | null;
}

export type HistoryRange = 'ALL' | 'TODAY' | 'YESTERDAY' | 'D7' | 'D15' | 'D30';

export interface RangeBounds {
  dateFrom?: string;
  dateTo?: string;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const DAYS_BACK: Record<Exclude<HistoryRange, 'ALL'>, number> = {
  TODAY: 0,
  YESTERDAY: 1,
  D7: 7,
  D15: 15,
  D30: 30,
};

const pad = (value: number) => String(value).padStart(2, '0');

const sum = (
  bets: HistoryBetLike[],
  pick: (_bet: HistoryBetLike) => number | undefined
) => bets.reduce((total, bet) => total + (pick(bet) || 0), 0);

/** Hand-rolled: the browser locale reshapes the built-in one. */
function clockTime(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Player's timezone. No seconds, year only when it isn't this one. */
export function formatRoundTime(
  iso: string,
  now: Date = new Date()
): RoundTime {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return { day: '', clock: '' };
  }

  const day = `${date.getDate()} ${MONTHS[date.getMonth()]}`;
  const year =
    date.getFullYear() === now.getFullYear() ? '' : ` ${date.getFullYear()}`;
  return { day: `${day}${year}`, clock: clockTime(date) };
}

/** Dates for the endpoint. Days start at midnight, so "7 days" means
    seven whole days. `ALL` sends nothing. */
export function rangeBounds(range: HistoryRange, now: Date): RangeBounds {
  if (range === 'ALL') {
    return {};
  }
  const today = new Date(now.getTime());
  today.setHours(0, 0, 0, 0);
  const from = new Date(today.getTime());
  from.setDate(from.getDate() - DAYS_BACK[range]);

  // The only range that ends before now.
  if (range === 'YESTERDAY') {
    return { dateFrom: from.toISOString(), dateTo: today.toISOString() };
  }
  return { dateFrom: from.toISOString() };
}

/** 5.2 becomes `5.20x`, nothing becomes `0x`. */
export function formatMultiplier(value: number): string {
  return value ? `${value.toFixed(2)}x` : '0x';
}

/** Signs the amount and prefixes the wallet's own symbol. */
export function formatNet(currencySymbol: string, net: number): string {
  return `${netSign(net)}${formatMoney(currencySymbol, Math.abs(net))}`;
}

/** Signed, but bare — the list names the currency once instead. */
export function formatNetAmount(net: number): string {
  return `${netSign(net)}${formatAmount(Math.abs(net))}`;
}

function netSign(net: number): string {
  if (net > 0) {
    return '+';
  }
  return net < 0 ? '-' : '';
}

/** How wide a money column has to be to hold `max`, counted in `ch`.
    The figures are tabular, so a `ch` is exactly one digit. */
export function moneyColumnCh(max: number, signed = false): number {
  return formatAmount(Math.abs(max)).length + (signed ? 1 : 0);
}

// Half a paisa either way is still break-even.
const resultOf = (net: number, voided: boolean): HistoryResult => {
  if (voided) {
    return 'void';
  }
  if (net > 0.005) {
    return 'win';
  }
  return net < -0.005 ? 'loss' : 'tie';
};

export interface HistoryRowOptions<T> {
  /** Defaults to the backend's selection name. */
  selectionOf?: (_item: T) => string | null;
}

export function toHistoryRow<T extends HistoryItemLike>(
  item: T,
  { selectionOf }: HistoryRowOptions<T> = {}
): HistoryRow {
  const bets = item.bets ?? [];
  const stake = sum(bets, (bet) => bet.amount);
  const payout = sum(bets, (bet) => bet.winAmount);
  const net = payout - stake;

  return {
    id: item.id,
    roundId: item.externalRoundId || item.roundId || item.id,
    time: formatRoundTime(item.roundTime ?? ''),
    stake,
    payout,
    net,
    multiplier: stake ? payout / stake : 0,
    result: resultOf(net, item.outcome === 'VOID'),
    selection: selectionOf
      ? selectionOf(item)
      : (bets[0]?.selectionName ?? null),
    betCount: bets.length,
  };
}

/** Each bet on its own line, in the order they were placed. */
export function toBetLines(item: HistoryItemLike): BetLine[] {
  return (item.bets ?? []).map((bet) => {
    const stake = bet.amount || 0;
    const payout = bet.winAmount || 0;
    const net = payout - stake;
    return {
      id: bet.id,
      stake,
      multiplier: stake ? payout / stake : 0,
      payout,
      net,
      result: resultOf(net, bet.status === 'VOID'),
      selection: bet.selectionName ?? null,
    };
  });
}

/** A gap in the page strip, drawn as dots. */
export const PAGE_GAP = 'gap';

export type PageItem = number | typeof PAGE_GAP;

/** How many chips fit before the strip needs dots. */
const MAX_CHIPS = 7;

/** Page chips to draw, zero-based. Near an end the strip runs five and
    dots the middle; otherwise it centres. */
export function pageWindow(page: number, totalPages: number): PageItem[] {
  if (totalPages <= MAX_CHIPS) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }
  const last = totalPages - 1;
  if (page <= 3) {
    return [0, 1, 2, 3, 4, PAGE_GAP, last];
  }
  if (page >= last - 3) {
    return [0, PAGE_GAP, last - 4, last - 3, last - 2, last - 1, last];
  }
  return [0, PAGE_GAP, page - 1, page, page + 1, PAGE_GAP, last];
}
